import React from 'react'
import { Navigate } from 'react-router-dom'

import routes from '@src/Routes/routes'
import { getLandingRoute } from '@helpers/getLandingRoute'
import { useGetShowCustomers } from '@helpers/hooks/useGetShowCustomers'
import { useGetMyAccount } from '@queries/Account'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm } from '@type/permissions'

interface TeamHomeProps {
  // The members roster, passed in so each router (web / MS Teams) keeps its
  // own lazy import rather than pulling the page into this shared chunk.
  roster: React.ReactNode
}

// Index route for the Team area. Members (team.view) is the default tab, but
// Departments/Locations (team.manage_org_structure) and Roles (role.view) are
// reachable without team.view. A role-admin without team.view must land
// somewhere usable rather than loop through getLandingRoute (which would point
// right back at /members). team.manage_org_structure implies team.view, so the
// only fallthrough is a roles-only admin → Roles. Shared by both shells so the
// Teams embed routes role-admins the same way the web app does.
export const TeamHome: React.FC<TeamHomeProps> = ({ roster }) => {
  const { data: user } = useGetMyAccount()
  const showCustomers = useGetShowCustomers()
  const canViewMembers = useHasPermission(Perm.TEAM_VIEW)
  const canViewRoles = useHasPermission(Perm.ROLE_VIEW)

  if (user === undefined) return null
  if (canViewMembers) return <>{roster}</>
  // A roles-only admin lands on the Roles tab — which renders the locked
  // "contact support" teaser when the beta flag is off (a valid destination),
  // rather than bouncing through getLandingRoute.
  if (canViewRoles) return <Navigate to={routes.private.teamRoles} replace />
  return <Navigate to={getLandingRoute(user, showCustomers)} replace />
}

export default TeamHome
